import { useQuery } from '@ltvco/refresh-lib/vendors';

import {
  type CourtCaseCluster,
  type DisplayOpinion,
  type HeadmatterOpinion,
  type OpinionsResponse,
} from '../../types';

import { DEFAULT_QUERY_OPTIONS, useBaseCourtApi } from './useBaseCourtApi';

export const useCourtOpinionDetails = (id: string | undefined) => {
  const {
    getCluster,
    getClusterOpinions,
    searchCaseLaw,
    isLoading: isTokenLoading,
  } = useBaseCourtApi();

  // Query for cluster information
  const clusterQuery = useQuery<CourtCaseCluster | null>(
    ['cluster', id],
    () => (id ? getCluster(id) : null),
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!id && !isTokenLoading,
    }
  );

  // Query for cluster opinions
  const opinionsQuery = useQuery<DisplayOpinion[]>(
    ['cluster-opinions', id],
    async () => {
      if (!id || !clusterQuery.data) {
        return [];
      }

      const opinions = await getClusterOpinions(id);

      // Add the cluster's headmatter as the first opinion
      const opinionsWithHeadmatter: DisplayOpinion[] = clusterQuery.data
        .headmatter
        ? [
            {
              id: -1, // Use negative ID to distinguish from real opinions
              type: 'headmatter' as const,
              html_with_citations: clusterQuery.data.headmatter,
              isHeadmatter: true as const,
              meta: {
                timestamp: clusterQuery.data.date_created,
                date_created: clusterQuery.data.date_created,
              },
            } as HeadmatterOpinion,
            ...opinions,
          ]
        : opinions;

      return opinionsWithHeadmatter;
    },
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!id && !!clusterQuery.data && !isTokenLoading,
    }
  );

  // Query for similar cases - use simple case name search (related: query causes 403)
  const similarCasesQuery = useQuery<OpinionsResponse | null>(
    [
      'similar-cases',
      clusterQuery.data?.case_name_short,
      clusterQuery.data?.case_name,
    ],
    async () => {
      if (!clusterQuery.data) {
        return null;
      }

      const caseName =
        clusterQuery.data.case_name_short || clusterQuery.data.case_name || '';
      if (!caseName.trim()) {
        return null;
      }

      // Simple query with case name in quotes - no related: or other keywords
      const query = `"${caseName.trim()}"`;
      const results = await searchCaseLaw(query, 'score desc');

      return {
        ...results,
        results: results.results.slice(0, 5),
      };
    },
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled:
        !!clusterQuery.data && !isTokenLoading && !opinionsQuery.isLoading,
    }
  );

  // Get display opinion IDs (excluding headmatter)
  const displayOpinionIds =
    opinionsQuery.data
      ?.filter((opinion) => opinion.type !== 'headmatter')
      .map((opinion) => opinion.id)
      .join(',') || '';

  return {
    clusterQuery,
    opinionsQuery,
    similarCasesQuery,
    displayOpinionIds,
    isLoading: clusterQuery.isLoading || isTokenLoading,
    isError: clusterQuery.isError,
    error: clusterQuery.error,
    data: clusterQuery.data,
  };
};
